import {
  Table,
  TextInput,
  Button,
} from '@mantine/core';
import { Page } from '@/components';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import validate from '@/helpers/validations';
import { useState, useEffect } from 'react';
import Loading from '@/components/Loading';
import { getMutationResult } from '@/helpers/query';
import { useUpdateReverseMortgageDataMutation, useGetReverseMortgageDataMutation } from '@/services/api';
import { useTranslation } from 'react-i18next';
import classes from './style.module.css';

const ReverseMortgage = () => {
  const { t } = useTranslation();
  const [loading, { open, close }] = useDisclosure(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [updateReverseMortgageDataMutationConfig] = useUpdateReverseMortgageDataMutation();
  const [getReverseMortgageDataMutationConfig] = useGetReverseMortgageDataMutation();
  const [companies, setCompanies] = useState([]); // Store the list of company names dynamically

  const fieldValidations = {
    presentage: (value) => validate(value, {
      required: t('globals.validations.required'),
      percentage: t('globals.validations.validPercent'),
    }),
  };

  const form = useForm({
    initialValues: {
      companies: {}, // Dynamically populated after fetching data
    },
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = { companies: {} };

      Object.keys(values.companies)?.forEach((company) => {
        const companyErrors = values.companies[company].map((row) => {
          const error = fieldValidations.presentage(row.value);
          return error ? { value: error } : null; // Return null if no error
        });

        // Only include errors for the company if there are any
        if (companyErrors.some((error) => error !== null)) {
          errors.companies[company] = companyErrors;
        }
      });

      // If no errors exist, return an empty object
      return Object.keys(errors.companies).length > 0 ? errors : {};
    },
  });

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const {
        data: getDataResponse,
        isError: getDataIsError,
      } = await getMutationResult({}, getReverseMortgageDataMutationConfig);

      if (getDataIsError) {
        notifications.show({
          title: 'שגיאה',
          message: 'לא ניתן לטעון את הנתונים',
          color: 'red',
        });
        setCompaniesLoading(false);
        return;
      }
      const { data } = getDataResponse;

      // Transform the data into the required structure
      const initialValues = {};
      const companyNames = data.map((company) => {
        initialValues[company.company] = company.data.map((item) => ({
          age: item.age,
          value: item.value.toString(), // Convert value to string for TextInput
        }));
        return company.company;
      });

      setCompanies(companyNames); // Store company names
      form.setValues({ companies: initialValues }); // Set initial values dynamically
    } catch (error) {
      notifications.show({
        title: 'שגיאה',
        message: 'לא ניתן לטעון את הנתונים',
        color: 'red',
      });
    }
    setCompaniesLoading(false);
  };
  // Fetch companies and their data from the server
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (form.validate().hasErrors) {
      notifications.show({
        title: 'שגיאה',
        message: 'יש למלא את כל השדות עם ערכים תקינים.',
        color: 'red',
      });
      return;
    }

    try {
      // Transform the form data back to the server's expected structure
      const formData = {
        companies: Object.keys(form.values.companies).map((company) => ({
          company,
          data: form.values.companies[company].map((row) => ({
            age: row.age,
            value: parseFloat(row.value), // Convert value back to number
          })),
        })),
      };

      const {
        data: updateResponse,
        isError: updateDataIsError,
      } = await getMutationResult({ ...formData }, updateReverseMortgageDataMutationConfig);

      open();

      if (updateDataIsError || !updateResponse) {
        notifications.show({
          title: 'המידע לא נשמר בהצלחה',
          message: 'המידע לא נשמר בהצלחה',
          color: 'red',
        });
        return;
      }

      notifications.show({
        title: 'המידע נשמר בהצלחה',
        message: 'המידע נשמר בהצלחה',
      });
    } catch (error) {
      notifications.show({
        title: 'המידע לא נשמר בהצלחה',
        message: 'המידע לא נשמר בהצלחה',
        color: 'red',
      });
    }
    close();
  };

  if (companiesLoading) return (<Loading />);

  return (
    <Page hideHelp hideNews title={t('pages.settings.pages.reverseMortgage.title')}>
      <div className={classes.container}>
        <Table>
          <thead>
            <tr>
              <th>גיל</th>
              {companies.map((company) => (
                <th key={company}>{company}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {form.values.companies[companies[0]]?.map((row, idx) => (
              <tr key={idx}>
                <td>{row.age}</td>
                {companies.map((company) => (
                  <td key={company}>
                    <TextInput
                      value={form.values.companies[company][idx].value}
                      onChange={(e) => form.setFieldValue(`companies.${company}.${idx}.value`, e.target.value)
                      }
                      onBlur={() => form.validateField(`companies.${company}.${idx}.value`)
                      }
                      rightSection={'%'}
                      // error={form.errors.companies?.[company]?.[idx]?.value}
                      error={
                        form.values.companies[company][idx]
                        && fieldValidations.presentage(
                          form.values.companies[company][idx].value,
                        )
                      }

                      required
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <div className={classes.buttonsWrapper}>
          <Button onClick={handleSubmit} loading={loading} className={classes.submitButton}>
            שמירה
          </Button>
        </div>
      </div>
    </Page>
  );
};

export default ReverseMortgage;
